import { useEffect, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Infinity as InfinityIcon, KeyRound, UserPlus } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useInvite } from '@/hooks/useInvite';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import { useStartMatch } from '@/hooks/useStartMatch';
import { AcceptInviteSheet } from '@/components/backgammon/AcceptInviteSheet';
import { BotSheet } from '@/components/backgammon/BotSheet';
import { CodiceInvitoSheet } from '@/components/backgammon/CodiceInvitoSheet';
import { InviteSheet } from '@/components/backgammon/InviteSheet';
import { type AmicoScelto, SceltaAmicoSheet } from '@/components/backgammon/SceltaAmicoSheet';
import { SfidaSheetDaCatalogo } from '@/components/social/SfidaSheet';
import { cn } from '@/lib/cn';
import { queueLabelKey } from '@/lib/qgammon/queueMode';
import type { MatchStatus } from '@/lib/qgammon/matchStatus';
import type { Player } from '@qlashgeminiz/qgammon-rules';

interface TesseraProps {
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  titolo: string;
  testId: string;
  inerte: boolean;
  onClick?: () => void;
  /** L'ultima tessera di una griglia DISPARI occupa tutta la riga (v. sotto). */
  intera?: boolean;
}

/**
 * Una tessera: icona e titolo, niente sottotitolo (Luca, I-282, 01/10: le quattro azioni «2 per riga»).
 * Icona SOPRA il titolo, e non accanto: a 360 px una tessera e' larga ~150 px, e «Senza orologio · fuori
 * classifica» accanto a un'icona andrebbe su tre righe (misura nel resoconto del giro).
 *
 * ⚠️ Una tessera SENZA azione (`onClick` assente) non e' solo `disabled`: si ATTENUA, perche' un bottone
 * spento che ha l'aspetto di uno acceso invita a un tocco che non fa niente (revisione di tma-orcr su
 * #532, punto 5: la regola era delle righe, e resta quella delle tessere). Con una ricerca in corso
 * (`inerte`) l'attenuazione e' gia' della griglia intera: qui non si somma, o la tessera scenderebbe a un
 * quinto. Appena l'azione c'e', la tessera torna quella di sempre.
 */
function Tessera({ Icon, titolo, testId, inerte, onClick, intera = false }: TesseraProps) {
  const senzaAzione = !onClick;
  return (
    <button
      type="button"
      data-testid={testId}
      data-senza-azione={senzaAzione || undefined}
      onClick={onClick}
      disabled={inerte || senzaAzione}
      className={cn(
        // Altezza e disposizione le decide il blocco (`.gioca-bg-tessere`/`.gioca-bg-tessera`, I-334 D): fra 44 e 96 px.
        'gioca-bg-tessera card-edge flex h-full w-full items-center justify-center rounded-2xl bg-bg-secondary px-2 text-center transition-opacity active:opacity-80 disabled:active:opacity-100',
        intera && 'col-span-2',
        senzaAzione && !inerte && 'opacity-45',
      )}
    >
      <Icon aria-hidden className="h-6 w-6 shrink-0 text-brand" />
      <span className="text-[13px] font-bold leading-tight text-fg">{titolo}</span>
    </button>
  );
}

interface AltriModiDiGiocareProps {
  /** Vero mentre una ricerca in coda e' in corso: le tessere si attenuano e non si premono. */
  inerte: boolean;
  /**
   * L'ingresso nella coda SENZA orologio e FUORI classifica (`RankedQueueEntry`, che possiede la coda).
   * Assente = serratura spenta = la tessera NON c'e' (non spenta: assente).
   */
  libera?: () => void;
}

/**
 * Le TESSERE della scheda «Gioca» del backgammon, sotto l'anello — Luca, I-282 (01/10), testuale:
 * «Remove the text "other ways to play". Add the pill per no ranked no clock coda, insieme alle altre 3
 * pills ora presenti. Queste 4 pills mettile 2 per riga». In quest'ordine:
 *   [Senza orologio · fuori classifica] [Gioca contro il bot]
 *   [Sfida un amico]                    [Codice invito]
 * ~~«Altri modi di giocare», tre righe spiegate (reference «Anello che respira», L-2606, 30/09)~~:
 * superato da I-282 — via il titolo, via i sottotitoli, e la coda libera entra nella griglia.
 *
 * ⚠️ **Serratura spenta = TRE tessere**: «2 per riga» con un numero dispari lascerebbe un buco a destra
 * dell'ultima. La scelta: l'ultima tessera di un numero dispari occupa TUTTA la riga (`col-span-2`) —
 * nessuna cella vuota che sembri un'azione mancante, e l'ordine resta quello di Luca.
 *
 * ⚠️ **Inerti mentre si cerca** (tavola `A-coda.dc.html`): una partita col bot o un invito aperti
 * durante una ricerca in coda producono due partite in parallelo, e il tetto delle partite aperte
 * del server lo direbbe solo dopo. Attenuate e non premibili, NON nascoste: restano la mappa di cosa
 * si puo' fare dopo. Vale anche per la tessera della coda libera: si e' gia' in una coda.
 * 📌 Dal 01/10 la scheda «Gioca» di prima (`BackgammonLobby`) NON e' montata da nessuna parte (I-282:
 * «la sezione play di oggi la nascondi»): i suoi ingressi vivono qui.
 *
 * ⚠️ **DUPLICATO, dichiarato**: amico e codice sono cablati qui ESATTAMENTE come in
 * `BackgammonLobby` — `useInvite`, l'effetto su `invite:accepted`, l'accettazione col codice,
 * e la sfida di piattaforma (`useSfidaDiPiattaforma` → `SceltaAmicoSheet` → `SfidaSheetDaCatalogo` con
 * `preselezionaBackgammon`, ripiego «preferisco un codice» → `InviteSheet`), con le stesse props: i
 * fogli di sfide-orcr non cambiano. La lobby resta su disco (non montata) finche' il proprietario della
 * TMA non decide se toglierla.
 */
export function AltriModiDiGiocare({ inerte, libera }: AltriModiDiGiocareProps) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const startMatch = useStartMatch();
  const [botAperto, setBotAperto] = useState(false);
  const [codiceAperto, setCodiceAperto] = useState(false);
  const invite = useInvite();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  // I-253 (T1-6), come in «Gioca»: «Sfida un amico» apre il selettore d'amici se la sfida di
  // piattaforma e' accesa (Limulo + ruolo beta), altrimenti direttamente l'invito a codice.
  const sfidaPiattaforma = useSfidaDiPiattaforma();
  const [scegliAmicoOpen, setScegliAmicoOpen] = useState(false);
  const [amicoSfidato, setAmicoSfidato] = useState<AmicoScelto | null>(null);

  // Lato CREATORE: il gameserver spinge `invite:accepted` (col NOSTRO colore) e si va alla partita,
  // col `status` di ADR-0114 (un appuntamento non e' «In gioco»). Come in `BackgammonLobby`.
  useEffect(() => {
    if (invite.status !== 'accepted' || !invite.accepted) return;
    haptics.notification('success');
    const { matchId, color, status } = invite.accepted;
    const timer = window.setTimeout(() => {
      setInviteOpen(false);
      startMatch({ game: 'qgammon', matchId, perspective: color, status });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [invite.status, invite.accepted, startMatch, haptics]);

  function apriBot() {
    haptics.impact('light');
    setBotAperto(true);
  }

  function apriAmico() {
    haptics.impact('light');
    if (sfidaPiattaforma) {
      setScegliAmicoOpen(true);
    } else {
      setInviteOpen(true);
    }
  }

  function apriCodice() {
    haptics.impact('light');
    setCodiceAperto(true);
  }

  function onAmicoScelto(amico: AmicoScelto) {
    setScegliAmicoOpen(false);
    setAmicoSfidato(amico);
  }

  /** Il ripiego dal selettore e dalla sfida: chi preferisce il codice trova il flusso di sempre. */
  function onPreferisciCodice() {
    setScegliAmicoOpen(false);
    setAmicoSfidato(null);
    setInviteOpen(true);
  }

  // Lato ACCETTANTE, col codice incollato a mano: il NOSTRO colore e lo `status` dalla risposta.
  function onInviteAccepted(result: { matchId: string; color: Player; status?: MatchStatus }) {
    haptics.notification('success');
    setAcceptOpen(false);
    startMatch({
      game: 'qgammon',
      matchId: result.matchId,
      perspective: result.color,
      status: result.status,
    });
  }

  return (
    <>
      <div
        data-testid="bg-altri-modi"
        aria-disabled={inerte || undefined}
        className={cn(
          'gioca-bg-tessere grid auto-rows-fr grid-cols-2 gap-2.5 transition-opacity',
          inerte && 'opacity-45',
        )}
      >
        {libera && (
          <Tessera
            Icon={InfinityIcon}
            testId="bg-unranked-join"
            titolo={t(queueLabelKey('unlimited'))}
            inerte={inerte}
            onClick={libera}
          />
        )}
        <Tessera
          Icon={Bot}
          testId="bg-altri-bot"
          titolo={t('ranked.other.botTitle')}
          inerte={inerte}
          onClick={apriBot}
        />
        <Tessera
          Icon={UserPlus}
          testId="bg-altri-amico"
          titolo={t('ranked.other.friendTitle')}
          inerte={inerte}
          onClick={apriAmico}
        />
        <Tessera
          Icon={KeyRound}
          testId="bg-altri-codice"
          titolo={t('ranked.other.codeTitle')}
          inerte={inerte}
          onClick={apriCodice}
          intera={!libera}
        />
      </div>

      {botAperto && <BotSheet onClose={() => setBotAperto(false)} />}

      {codiceAperto && (
        <CodiceInvitoSheet
          onCrea={() => {
            setCodiceAperto(false);
            setInviteOpen(true);
          }}
          onHoCodice={() => {
            setCodiceAperto(false);
            setAcceptOpen(true);
          }}
          onClose={() => setCodiceAperto(false)}
        />
      )}

      {inviteOpen && (
        <InviteSheet
          invite={invite}
          onClose={() => {
            // L'annullamento di un invito pendente lo fa il foglio stesso.
            setInviteOpen(false);
          }}
        />
      )}

      {acceptOpen && (
        <AcceptInviteSheet onAccepted={onInviteAccepted} onClose={() => setAcceptOpen(false)} />
      )}

      {scegliAmicoOpen && (
        <SceltaAmicoSheet
          onScegli={onAmicoScelto}
          onPreferisciCodice={onPreferisciCodice}
          onClose={() => setScegliAmicoOpen(false)}
        />
      )}

      {amicoSfidato && (
        <SfidaSheetDaCatalogo
          avversario={amicoSfidato.nome}
          amicoId={amicoSfidato.userId}
          preselezionaBackgammon
          onPreferisciCodice={onPreferisciCodice}
          onClose={() => setAmicoSfidato(null)}
        />
      )}
    </>
  );
}
